import { Platform } from "react-native";
import { logAnalyticsEvent } from "./analytics";
import { logAndFlush } from "./batch-logger";
import { sampleMemoryMetrics, sampleNetworkMetrics } from "./telemetry-monitor";

/**
 * Origin source triggering the media send process.
 */
export type MediaSendSource =
  | "share_intent"
  | "camera_capture"
  | "file_picker"
  | "drag_and_drop"
  | "audio_record";

/**
 * Interface representing an active media send session with timing and diagnostic state.
 */
export interface IMediaSendSession {
  /** Unique session tracking identifier correlating client logs and backend telemetry */
  sessionId: string;
  /** Ingestion source type */
  source: MediaSendSource;
  /** Target execution platform ('android' | 'ios' | 'web') */
  platform: "android" | "ios" | "web";
  /** Current item index in batch (1-indexed) */
  itemIndex: number;
  /** Total items in upload batch */
  totalItems: number;
  /** Original or assigned file name */
  fileName: string;
  /** Media MIME type (e.g. 'image/jpeg', 'video/mp4') */
  mimeType: string;
  /** File size in bytes */
  fileSizeBytes: number;
  /** Epoch timestamp when the session began */
  startTime: number;
  /** Performance monotonic timestamp when the current step began */
  stepStartTime: number;
  /** Internal cache file path on Android/iOS if copied to disk */
  cachedFilePath?: string;
  /** Effective network connection type (e.g. 'wifi', '4g', '3g') */
  networkType?: string;
  /** Estimated network round-trip time in milliseconds */
  networkRttMs?: number;
  /** Initial JS heap memory usage in bytes */
  startMemoryBytes?: number;
}

/**
 * Generates a random UUID v4 for telemetry session identification.
 *
 * @returns {string} Unique identifier string.
 */
function generateSessionId(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Initiates a new media send telemetry session, recording initial device and network state.
 * Emits the 'media_send_start' event to Google Analytics and logs to batch logger.
 *
 * @param {object} params - Initialization configuration.
 * @param {MediaSendSource} params.source - Origin of the media send action.
 * @param {number} [params.totalItems=1] - Total items in this batch.
 * @param {number} [params.itemIndex=1] - Index of this item (1-indexed).
 * @param {string} [params.fileName="media_file"] - File name.
 * @param {string} [params.mimeType="image/jpeg"] - Content MIME type.
 * @param {number} [params.fileSizeBytes=0] - File size in bytes.
 * @returns {IMediaSendSession} Active session tracking context.
 */
export function startMediaSendSession(params: {
  source: MediaSendSource;
  totalItems?: number;
  itemIndex?: number;
  fileName?: string;
  mimeType?: string;
  fileSizeBytes?: number;
}): IMediaSendSession {
  const sessionId = generateSessionId();
  const platform = Platform.OS === "android" ? "android" : Platform.OS === "ios" ? "ios" : "web";
  const now = performance.now();

  const mem = sampleMemoryMetrics();
  const net = sampleNetworkMetrics();

  const session: IMediaSendSession = {
    sessionId,
    source: params.source,
    platform,
    itemIndex: params.itemIndex ?? 1,
    totalItems: params.totalItems ?? 1,
    fileName: params.fileName || `media_${Date.now()}`,
    mimeType: params.mimeType || "image/jpeg",
    fileSizeBytes: params.fileSizeBytes || 0,
    startTime: now,
    stepStartTime: now,
    networkType: net.networkEffectiveType || "unknown",
    networkRttMs: net.networkRttMs,
    startMemoryBytes: mem.memoryUsageBytes,
  };

  logAnalyticsEvent("media_send_start", {
    session_id: session.sessionId,
    source: session.source,
    platform: session.platform,
    item_index: session.itemIndex,
    total_items: session.totalItems,
    file_name: session.fileName,
    mime_type: session.mimeType,
    file_size_bytes: session.fileSizeBytes,
    network_type: session.networkType,
    network_rtt_ms: session.networkRttMs,
  });

  logAndFlush(
    "info",
    `[MediaTelemetry] [${session.platform.toUpperCase()}] Start send session ${session.sessionId.slice(0, 8)} (${session.itemIndex}/${session.totalItems}): "${session.fileName}" (${session.mimeType}, ${session.fileSizeBytes}B, net=${session.networkType})`
  );

  return session;
}

/**
 * Records the completion of the native disk cache write step (Step 2: getCacheDir / CachesDirectory).
 *
 * @param {IMediaSendSession} session - The active tracking session.
 * @param {object} params - Cache details.
 * @param {string} params.cachePath - Absolute path where the media was cached.
 * @param {number} params.bytes - Byte length of cached payload.
 * @param {boolean} [params.success=true] - Whether the write succeeded.
 * @param {string} [params.error] - Error message if write failed.
 */
export function logMediaSendCacheStep(
  session: IMediaSendSession,
  params: { cachePath: string; bytes: number; success?: boolean; error?: string }
): void {
  const now = performance.now();
  const cacheDurationMs = Math.round(now - session.stepStartTime);
  session.stepStartTime = now;
  session.cachedFilePath = params.cachePath;
  if (params.bytes > 0 && session.fileSizeBytes === 0) {
    session.fileSizeBytes = params.bytes;
  }

  const scheme = params.cachePath.startsWith("content://")
    ? "content"
    : params.cachePath.startsWith("file://")
      ? "file"
      : "local";

  logAnalyticsEvent("media_send_cache_write", {
    session_id: session.sessionId,
    platform: session.platform,
    step_latency_ms: cacheDurationMs,
    cached_bytes: params.bytes,
    cache_path_scheme: scheme,
    success: params.success !== false,
  });

  logAndFlush(
    params.success !== false ? "info" : "error",
    `[MediaTelemetry] [${session.platform.toUpperCase()}] Cache write completed in ${cacheDurationMs}ms (${params.bytes}B, scheme=${scheme})${params.error ? ` Error: ${params.error}` : ""}`
  );
}

/**
 * Records the completion of metadata and geolocation extraction (Step 3/4).
 *
 * @param {IMediaSendSession} session - The active tracking session.
 * @param {object} params - Extracted attributes.
 * @param {boolean} params.hasGps - Whether GPS geotagging was resolved.
 * @param {boolean} params.hasExif - Whether EXIF headers were parsed.
 * @param {string} [params.locationLabel] - Resolved human-readable location.
 */
export function logMediaSendMetadataStep(
  session: IMediaSendSession,
  params: { hasGps: boolean; hasExif: boolean; locationLabel?: string }
): void {
  const now = performance.now();
  const stepDurationMs = Math.round(now - session.stepStartTime);
  session.stepStartTime = now;

  logAnalyticsEvent("media_send_metadata", {
    session_id: session.sessionId,
    platform: session.platform,
    step_latency_ms: stepDurationMs,
    has_gps: params.hasGps ? 1 : 0,
    has_exif: params.hasExif ? 1 : 0,
    location_label: params.locationLabel,
  });

  logAndFlush(
    "info",
    `[MediaTelemetry] [${session.platform.toUpperCase()}] Metadata extracted in ${stepDurationMs}ms (gps=${params.hasGps}, exif=${params.hasExif}${params.locationLabel ? `, loc="${params.locationLabel}"` : ""})`
  );
}

/**
 * Records the start of network payload dispatch to Cloud Functions (Step 5).
 * Samples current connection bandwidth and RTT parameters.
 *
 * @param {IMediaSendSession} session - The active tracking session.
 * @param {number} [payloadBytes] - Total base64/binary bytes being transmitted.
 */
export function logMediaSendNetworkStart(session: IMediaSendSession, payloadBytes?: number): void {
  const now = performance.now();
  session.stepStartTime = now;

  const net = sampleNetworkMetrics();
  const mem = sampleMemoryMetrics();

  session.networkType = net.networkEffectiveType || session.networkType || "unknown";
  session.networkRttMs = net.networkRttMs ?? session.networkRttMs;

  const size = payloadBytes || session.fileSizeBytes;

  logAnalyticsEvent("media_send_upload_start", {
    session_id: session.sessionId,
    platform: session.platform,
    payload_size_bytes: size,
    network_type: session.networkType,
    network_rtt_ms: session.networkRttMs,
    memory_used_mb: mem.memoryUsageBytes ? Math.round(mem.memoryUsageBytes / (1024 * 1024)) : undefined,
  });

  logAndFlush(
    "info",
    `[MediaTelemetry] [${session.platform.toUpperCase()}] Network upload started (${size}B, net=${session.networkType}, rtt=${session.networkRttMs ?? "unknown"}ms)`
  );
}

/**
 * Records successful media upload completion, calculating throughput and total latency.
 *
 * @param {IMediaSendSession} session - The active tracking session.
 * @param {object} params - Completion details.
 * @param {string} [params.mediaId] - Created Media document ID.
 * @param {boolean} [params.isDuplicate=false] - Whether upload matched pre-existing duplicate hash.
 * @param {object} [params.serverDiagnostics] - Timings reported by Cloud Function.
 * @param {boolean} [params.cacheCleaned=true] - Whether internal cache file was purged.
 */
export function logMediaSendComplete(
  session: IMediaSendSession,
  params: {
    mediaId?: string;
    isDuplicate?: boolean;
    serverDiagnostics?: {
      serverProcessingMs?: number;
      safetyScanMs?: number;
      storageCommitMs?: number;
      firestoreCommitMs?: number;
    };
    cacheCleaned?: boolean;
  }
): void {
  const now = performance.now();
  const uploadDurationMs = Math.round(now - session.stepStartTime);
  const totalDurationMs = Math.round(now - session.startTime);

  const throughputKbps =
    session.fileSizeBytes > 0 && uploadDurationMs > 0
      ? Math.round((session.fileSizeBytes / (uploadDurationMs / 1000)) / 1024)
      : 0;

  const diag = params.serverDiagnostics;

  logAnalyticsEvent("media_send_complete", {
    session_id: session.sessionId,
    platform: session.platform,
    media_id: params.mediaId,
    is_duplicate: params.isDuplicate ? 1 : 0,
    file_size_bytes: session.fileSizeBytes,
    upload_duration_ms: uploadDurationMs,
    total_duration_ms: totalDurationMs,
    throughput_kbps: throughputKbps,
    network_type: session.networkType,
    server_processing_ms: diag?.serverProcessingMs,
    safety_scan_ms: diag?.safetyScanMs,
    storage_commit_ms: diag?.storageCommitMs,
    firestore_commit_ms: diag?.firestoreCommitMs,
    cache_cleaned: params.cacheCleaned !== false ? 1 : 0,
  });

  logAndFlush(
    "info",
    `[MediaTelemetry] [${session.platform.toUpperCase()}] Media send SUCCESS: total=${totalDurationMs}ms upload=${uploadDurationMs}ms throughput=${throughputKbps}kB/s isDup=${Boolean(params.isDuplicate)} mediaId=${params.mediaId || "unknown"}${diag ? ` (serverTotal=${diag.serverProcessingMs}ms safety=${diag.safetyScanMs}ms storage=${diag.storageCommitMs}ms firestore=${diag.firestoreCommitMs}ms)` : ""}`
  );
}

/**
 * Records a failure at any stage of the media send process.
 *
 * @param {IMediaSendSession} session - The active tracking session.
 * @param {object} params - Error details.
 * @param {string} params.step - The step that failed ('read', 'cache', 'metadata', 'upload', 'server').
 * @param {string} params.errorMessage - Description of the failure.
 * @param {string} [params.errorCode] - Diagnostic error code if available.
 */
export function logMediaSendFailure(
  session: IMediaSendSession,
  params: {
    step: "read" | "cache" | "metadata" | "upload" | "server" | "queue";
    errorMessage: string;
    errorCode?: string;
  }
): void {
  const now = performance.now();
  const stepDurationMs = Math.round(now - session.stepStartTime);
  const totalDurationMs = Math.round(now - session.startTime);

  logAnalyticsEvent("media_send_fail", {
    session_id: session.sessionId,
    platform: session.platform,
    failed_step: params.step,
    step_duration_ms: stepDurationMs,
    total_duration_ms: totalDurationMs,
    file_size_bytes: session.fileSizeBytes,
    error_message: params.errorMessage.slice(0, 100),
    error_code: params.errorCode || "GENERIC_ERROR",
    network_type: session.networkType,
  });

  logAndFlush(
    "error",
    `[MediaTelemetry] [${session.platform.toUpperCase()}] Media send FAILED at step '${params.step}' after ${totalDurationMs}ms: ${params.errorMessage} (code=${params.errorCode || "unknown"})`
  );
}
